import { MetricQueryString } from "@/entities/Finance/models/MetricQueryString.model";
import { MetricConfig } from "@/features/Metrics/Metrics.types";
import RightSidebarButton from "@/features/Navigation/components/RightSidebarButton";
import clsx from "clsx";
import { AIMContextViewAtom } from "./AIMPanelContext";
import { useAtomValue, useSetAtom } from "jotai";
import { PanelSkeleton } from "./PanelSkeleton";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { getEvalCtxLineageMetricByQuery } from "@/entities/Finance/Metric.queries";
import { asOfDateAtom } from "@/features/Metrics/state/Metrics.state";
import { ParsedLineageExpression } from "../../MetricsLab/ParsedLineageExpression";
import { MetricInfoDisplay } from "@/features/Metrics/components/MetricInfoDisplayV2";

export interface EvaluatedCtxLineagePanelProps {
    query: MetricQueryString;
    config: MetricConfig;
    depth?: number;
    title?: string;
}

export function EvaluatedCtxLineagePanel({
    query,
    config,
    title,
    depth = 1,
}: EvaluatedCtxLineagePanelProps) {
    const asOfDate = useAtomValue(asOfDateAtom);
    const setAIMContextView = useSetAtom(AIMContextViewAtom);
    const { data: metric, isLoading } = useApiQuery(
        getEvalCtxLineageMetricByQuery,
        {
            params: {
                metricQuery: query,
                fromDate: asOfDate
                    .subtract(12, "month")
                    .startOf("month")
                    .utc()
                    .startOf("day")
                    .toISOString(),
                toDate: asOfDate.utc().endOf("day").toISOString(),
            },
        },
    );

    if (isLoading) return <PanelSkeleton title={title} />;

    const latestValue = metric?.latest();
    const metricDescription = metric?.getMetricDescription();

    return (
        <div
            className={clsx(
                "px-6 pb-6",
                "custom-scrollbar absolute inset-0 flex min-h-0 flex-col overflow-hidden",
            )}
        >
            <div className="sticky my-4 flex flex-col">
                <RightSidebarButton
                    onClick={() => {
                        setAIMContextView(null);
                    }}
                />
                <h2 className="pageheading">{title || config.label}</h2>
                {metricDescription && (
                    <MetricInfoDisplay metricDescription={metricDescription} />
                )}
            </div>
            <div className="flex flex-col gap-4">
                {latestValue && (
                    <ParsedLineageExpression
                        expression={latestValue.parseExpression(depth)}
                        metricType={config.type}
                    />
                )}
            </div>
        </div>
    );
}
